/**
 * Server-Sent Events client built on `fetch` + `ReadableStream` rather than the
 * native `EventSource`.
 *
 * Why not `EventSource`? It can't send an `Authorization` header, and this app
 * is bearer-token (MSAL) end-to-end. Consuming the stream via `fetch` lets us
 * attach the token like every other API call, and avoids putting tokens in the
 * query string (where they'd land in access logs).
 *
 * The stream is a lightweight "something changed" poke — the caller re-fetches
 * the real data through the authorized REST endpoint. We reconnect with
 * exponential backoff until the caller's AbortSignal fires.
 */

const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface SseEvent {
  /** The SSE event type (`event:` line), defaulting to "message". */
  event: string;
  /** The concatenated `data:` payload. */
  data: string;
}

export interface SseStreamOptions {
  getAccessToken: () => Promise<string>;
  signal: AbortSignal;
  onEvent: (event: SseEvent) => void;
  /** Optional hook for surfacing connection errors (e.g. logging). */
  onError?: (err: unknown) => void;
}

/** Open an SSE stream and pump events to `onEvent` until `signal` aborts. */
export async function streamSse(path: string, opts: SseStreamOptions): Promise<void> {
  let attempt = 0;

  while (!opts.signal.aborted) {
    try {
      const token = await opts.getAccessToken();
      const response = await fetch(`${base}${path}`, {
        method: 'GET',
        headers: { Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
        signal: opts.signal
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE ${path} → ${response.status}`);
      }

      attempt = 0; // connected — reset backoff
      await pump(response.body, opts.onEvent);
      // Stream ended without error (server closed / heartbeat gap) — fall
      // through to reconnect unless the caller aborted in the meantime.
    } catch (err) {
      if (opts.signal.aborted) return;
      opts.onError?.(err);
    }

    if (opts.signal.aborted) return;
    attempt += 1;
    const delay = Math.min(1000 * 2 ** Math.min(attempt, 5), 30_000);
    await sleep(delay, opts.signal);
  }
}

/** Read the response body, splitting on SSE frame boundaries (blank line). */
async function pump(body: ReadableStream<Uint8Array>, onEvent: (event: SseEvent) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const evt = parseFrame(frame);
        if (evt) onEvent(evt);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.cancel().catch(() => {
      /* stream already torn down */
    });
  }
}

/** Parse one SSE frame. Returns null for comment-only frames (`: keep-alive`). */
function parseFrame(frame: string): SseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of frame.split('\n')) {
    if (line === '' || line.startsWith(':')) continue; // comment / keep-alive
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/** A cancellable delay that resolves early if `signal` aborts. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}
